import React, { useState } from 'react';
import { Container } from '../../styles/Styles';
import { useQuery } from '@apollo/client';
import { GET_COORDINATOR_TOURS } from '../gql/tours-gql';
import { useAuth } from '../../context/AuthContext';
import { Roles } from '../common/roles';
import { Navigate } from 'react-router-dom';
import moment from 'moment/moment';
import styled from 'styled-components';
import Loader from '../common/Loader';
import CoordinatorNav from './CoordinatorNav';
import TourCalendar from '../common/TourCalendar';

const TourCard = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    margin-bottom: 1rem;
`;

const TourHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
`;

const TourTitle = styled.h3`
    margin: 0;
    color: #333;
    font-size: 1.2rem;
`;

const TourDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const DetailLabel = styled.span`
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 0.25rem;
`;

const DetailValue = styled.span`
    font-weight: 500;
    color: #333;
`;

const EmptyState = styled.p`
    margin-top: 1rem;
    color: #666;
    text-align: center;
`;

const CoordinatorDashboard = () => {
    const { user, isAuthenticated } = useAuth();
    const [currentMonth, setCurrentMonth] = useState(() => new Date());

    const toursQueryVars = {
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear()
    };

    const { data: toursList, loading } = useQuery(GET_COORDINATOR_TOURS, {
        variables: toursQueryVars
    });
    const coordinatorTours = (toursList?.getCoordinatorTours || []).map((tour: any) => ({
        ...tour,
        title: tour.tourTitle,
    }));

    const renderTourCard = (tour: any) => {
        const guests = tour.guests || [];
        const totals = guests.reduce(
            (acc: { adults: number; youths: number; children: number; enfants: number }, guest: any) => ({
                adults: acc.adults + (guest.clientAdult || 0),
                youths: acc.youths + (guest.clientYouth || 0),
                children: acc.children + (guest.clientChild || 0),
                enfants: acc.enfants + (guest.clientEnfant || 0),
            }),
            { adults: 0, youths: 0, children: 0, enfants: 0 }
        );
        const totalCustomers = totals.adults + totals.youths + totals.children + totals.enfants;

        return (
            <TourCard>
                <TourHeader>
                    <TourTitle>{tour.title || tour.tourTitle}</TourTitle>
                </TourHeader>
                <TourDetails>
                    <DetailItem>
                        <DetailLabel>Date</DetailLabel>
                        <DetailValue>{moment(tour.bookingDate).format("D MMM YYYY h:mm a")}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Customers</DetailLabel>
                        <DetailValue>{totalCustomers}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Adults</DetailLabel>
                        <DetailValue>{totals.adults}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Youths</DetailLabel>
                        <DetailValue>{totals.youths}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Children</DetailLabel>
                        <DetailValue>{totals.children}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Enfants</DetailLabel>
                        <DetailValue>{totals.enfants}</DetailValue>
                    </DetailItem>
                </TourDetails>
                {guests.length > 0 && (
                    <div>
                        <h4>Guests</h4>
                        {guests.map((guest: any, index: number) => (
                            <DetailItem key={index}>
                                <DetailValue>
                                    {guest.clientName || 'Guest'} — Adults: {guest.clientAdult || 0},
                                    Youths: {guest.clientYouth || 0},
                                    Children: {guest.clientChild || 0},
                                    Enfants: {guest.clientEnfant || 0}
                                </DetailValue>
                            </DetailItem>
                        ))}
                    </div>
                )}
            </TourCard>
        );
    };

    if (!isAuthenticated || user?.role[0] !== Roles.COORDINATOR) {
        return <Navigate to="/" />;
    }

    if (loading) return <Loader />;

    const handleMonthChange = (date: Date) => {
        setCurrentMonth(date);
    };

    return (
        <Container>
            <CoordinatorNav />
            <h2>Coordinator Dashboard</h2>

            <TourCalendar
                tours={coordinatorTours}
                renderTourCard={renderTourCard}
                onMonthChange={handleMonthChange}
                currentDate={currentMonth}
            />
            {!loading && coordinatorTours.length === 0 && (
                <EmptyState>No tours assigned for this month.</EmptyState>
            )}
        </Container>
    );
};

export default CoordinatorDashboard;
